// Mapping between design space and the rendered template, used by the
// on-mockup placement overlay. Template coordinates are output pixels of the
// compiled (preview) template.

/**
 * Forward map: design-space fraction (u, v) -> template pixel. Outside
 * [0, 1] it continues straight along the surface's edge direction; the grid's
 * bilinear cells would curve away quadratically there and throw the frame
 * across the canvas.
 */
export function forwardMap(template, u, v) {
  const cu = Math.max(0, Math.min(1, u)), cv = Math.max(0, Math.min(1, v));
  const p = gridAt(template, cu, cv);
  if (cu === u && cv === v) return p;
  const h = 1 / template.gridSize;
  const du = cu === u ? null : slope(template, cu, cv, h, 0);
  const dv = cv === v ? null : slope(template, cu, cv, 0, h);
  let x = p.x, y = p.y;
  if (du) {
    x += du.x * (u - cu);
    y += du.y * (u - cu);
  }
  if (dv) {
    x += dv.x * (v - cv);
    y += dv.y * (v - cv);
  }
  return { x, y };
}

// Derivative of the grid at a boundary point, taken over one cell inwards.
function slope(template, u, v, hu, hv) {
  const u0 = Math.max(0, u - hu), v0 = Math.max(0, v - hv);
  const u1 = Math.min(1, u0 + hu), v1 = Math.min(1, v0 + hv);
  const a = gridAt(template, u0, v0), b = gridAt(template, u1, v1);
  const d = hu ? u1 - u0 : v1 - v0;
  return { x: (b.x - a.x) / d, y: (b.y - a.y) / d };
}

function gridAt(template, u, v) {
  const n = template.gridSize, g = template.grid;
  const fu = u * n, fv = v * n;
  const i = Math.min(n - 1, Math.floor(fu)), j = Math.min(n - 1, Math.floor(fv));
  const a = fu - i, b = fv - j;
  const at = (ii, jj) => {
    const k = (jj * (n + 1) + ii) * 2;
    return [g[k], g[k + 1]];
  };
  const p00 = at(i, j), p10 = at(i + 1, j), p01 = at(i, j + 1), p11 = at(i + 1, j + 1);
  return {
    x: p00[0] * (1 - a) * (1 - b) + p10[0] * a * (1 - b) + p01[0] * (1 - a) * b + p11[0] * a * b,
    y: p00[1] * (1 - a) * (1 - b) + p10[1] * a * (1 - b) + p01[1] * (1 - a) * b + p11[1] * a * b,
  };
}

/** Design-space pixel -> template pixel. */
export function designToTemplate(template, pt) {
  return forwardMap(template, pt.x / template.designWidth, pt.y / template.designHeight);
}

/** Template pixel -> design-space pixel when the pixel shows the surface, else null. */
export function templateToDesign(template, x, y) {
  const px = Math.floor(x), py = Math.floor(y);
  if (px < 0 || py < 0 || px >= template.width || py >= template.height) return null;
  const p = py * template.width + px;
  if (template.coverage[p] < 8) return null;
  const q = p * 4, uv = template.uv;
  const u = (uv[q] * 256 + uv[q + 1]) / 65535, v = (uv[q + 2] * 256 + uv[q + 3]) / 65535;
  return { x: u * template.designWidth, y: v * template.designHeight };
}

/**
 * Converts a small template-pixel delta near a design point into a
 * design-space delta, using the local Jacobian of the forward map.
 */
export function templateDeltaToDesign(template, at, dx, dy) {
  const h = Math.max(template.designWidth, template.designHeight) * 0.01;
  const p = designToTemplate(template, at);
  const px = designToTemplate(template, { x: at.x + h, y: at.y });
  const py = designToTemplate(template, { x: at.x, y: at.y + h });
  const a = (px.x - p.x) / h, b = (py.x - p.x) / h, c = (px.y - p.y) / h, d = (py.y - p.y) / h;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-9) return { x: 0, y: 0 };
  return { x: (d * dx - b * dy) / det, y: (-c * dx + a * dy) / det };
}

/** Bounding box of the covered surface in template pixels. */
export function surfaceBounds(template) {
  let x0 = template.width, y0 = template.height, x1 = 0, y1 = 0;
  const step = 2;
  for (let y = 0; y < template.height; y += step) {
    for (let x = 0; x < template.width; x += step) {
      if (template.coverage[y * template.width + x] > 8) {
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x > x1) x1 = x;
        if (y > y1) y1 = y;
      }
    }
  }
  return x1 >= x0 ? { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } : { x: 0, y: 0, width: template.width, height: template.height };
}
